import React, { useState, useEffect } from 'react';

import EventTracking from 'lib/EventTracking';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CancelCoachingSessionInstance from 'coaching/components/CoachingHomeView/CancelCoachingSessionInstance';
import ConfirmCancelledRecurringSessionOrInstance from 'coaching/components/CoachingHomeView/ConfirmCancelledRecurringSessionOrInstance';
import SidebarHeader from 'coaching/components/SidebarHeader';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import SidebarFooter from 'components/SidebarFooter';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import { CoachingSessionInstance } from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import { deleteCoachingSession } from 'coaching/actions/CoachingActions';
import GET_COACHING_HOME_DASHBOARD_QUERY from 'coaching/queries/GetCoachingHomeDashboard.gql';
import { wouldBeLateCancellation } from 'coaching/components/CoachingHomeView/CoachingSessionInstanceRowComponents';
import { TimeslotFrequencyInput } from 'globalTypes';

type CancellationSteps = 'cancel-session' | 'confirmation';

export type Props = {
  sessionInstance: CoachingSessionInstance;
  onClose: () => void;
  onClickBookSession: () => void;
};

export default function CancelCoachingSessionInstanceSidebar({
  sessionInstance,
  onClose,
  onClickBookSession,
}: Props) {
  const [step, setStep] = useState<CancellationSteps>('cancel-session');
  const [cancellationReason, setCancellationReason] = useState<string>();
  const [cancellationDetails, setCancellationDetails] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const isLateCancellation = wouldBeLateCancellation(sessionInstance.startDateTime);

  const onCancellationSuccess = () => {
    setStep('confirmation');
  };

  useEffect(() => {
    // reset state when the session instance changes
    setStep('cancel-session');
  }, [sessionInstance.uuid]);

  const eventPrefix = `${coachingHomeEventPrefix}.cancel_instance_sidebar`;
  function trackKey(key: string) {
    EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  async function handleSubmit() {
    if (cancellationReason) {
      setSubmitting(true);
      await deleteCoachingSession(
        sessionInstance.coachingSessionScheduleUuid,
        cancellationReason,
        sessionInstance.uuid,
        undefined,
        [
          {
            query: GET_COACHING_HOME_DASHBOARD_QUERY,
            variables: {},
          },
        ],
        onCancellationSuccess
      );
      setSubmitting(false);
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to delete a session without specifying a cancellation reason. Please try again.'
      );
    }
  }

  const stepToComponentMap: Record<CancellationSteps, React.ReactElement> = {
    'cancel-session': (
      <>
        <CancelCoachingSessionInstance
          data-test="cancel-session-step"
          eventPrefix={eventPrefix}
          instanceUuid={sessionInstance.uuid}
          instanceStartDateTime={sessionInstance.startDateTime}
          wouldBeLateCancellation={isLateCancellation}
          isFreeTrial={sessionInstance.isFreeTrial}
          onSelectCancellationReason={setCancellationReason}
          onChangeCancellationDetails={setCancellationDetails}
        />
        <SidebarFooter>
          <BoxBody size="small">
            <BillingExplanation
              data-test="billing-explanation"
              isFreeTrial={sessionInstance.isFreeTrial}
              wouldBeLateCancellation={isLateCancellation}
              duration={sessionInstance.duration}
            />
            <Button
              full
              color="apple"
              data-test="cancel-session-button"
              eventKey={`${eventPrefix}.cancel_session.click`}
              eventProperties={
                cancellationDetails && {
                  data: cancellationDetails,
                }
              }
              disabled={!cancellationReason || submitting}
              loading={submitting}
              onClick={handleSubmit}>
              Cancel session
            </Button>
          </BoxBody>
        </SidebarFooter>
      </>
    ),
    confirmation: (
      <>
        <ConfirmCancelledRecurringSessionOrInstance
          data-test="confirm-cancelled-instance-step"
          eventPrefix={eventPrefix}
          frequency={TimeslotFrequencyInput.once}
          onClickBookSession={onClickBookSession}
        />
        <SidebarFooter noShadow>
          <BoxBody>
            <Link weight="semibold" href={LinkConstants.COACHING_HELP_SCHEDULING} target="_blank">
              <Text size="xsmall">Review our scheduling policy</Text>
            </Link>
          </BoxBody>
        </SidebarFooter>
      </>
    ),
  };

  return (
    <>
      <SidebarHeader title="Cancel session" eventPrefix={eventPrefix} onClose={onClose} />
      {stepToComponentMap[step]}
    </>
  );
}

function BillingExplanation({
  wouldBeLateCancellation,
  isFreeTrial,
  duration,
}: {
  wouldBeLateCancellation: boolean;
  isFreeTrial: boolean;
  duration: string;
}) {
  const billingNotice =
    wouldBeLateCancellation && !isFreeTrial
      ? `You will be charged $${duration} for the session.`
      : 'You will not be charged for this session.';
  return <Row size="small">Your session will be cancelled. {billingNotice}</Row>;
}
